import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import './DungeonLootConfig.scss';

interface LootTableEntry {
  baseId: number;
  weight: number;
  minCount: number;
  maxCount: number;
}

interface CatalogEntry {
  id: number;
  n: string; // display name
  t: string; // espm record type, e.g. "WEAP"
}

interface ShowEventDetail {
  dungeonId: string;
  refrId: number;
  current: LootTableEntry[];
  catalog?: CatalogEntry[];
  catalogReady?: boolean;
}

// Row-local editable form of a LootTableEntry - baseId is edited as a hex
// string (the usual way formIds are referred to in Skyrim modding, e.g.
// "0000000F" for Gold001) and only converted to a number on submit. `label`
// is normally auto-filled by picking a catalog entry (see the name search
// below), but stays a free-editable field so an item missing from the
// catalog (ESL-plugin items, anything the server's scan couldn't resolve a
// name for) can still be entered by hand via its raw baseId.
interface EditableRow {
  key: number;
  label: string;
  baseIdHex: string;
  weight: string;
  minCount: string;
  maxCount: string;
}

const MAX_SUGGESTIONS = 30;

let nextRowKey = 1;

function toRow(entry: LootTableEntry & { label?: string }): EditableRow {
  return {
    key: nextRowKey++,
    label: entry.label ?? '',
    baseIdHex: entry.baseId.toString(16).padStart(8, '0'),
    weight: String(entry.weight),
    minCount: String(entry.minCount),
    maxCount: String(entry.maxCount),
  };
}

function emptyRow(): EditableRow {
  return { key: nextRowKey++, label: '', baseIdHex: '', weight: '1', minCount: '1', maxCount: '1' };
}

const DungeonLootConfig: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [dungeonId, setDungeonId] = useState<string>('');
  const [refrId, setRefrId] = useState<number | null>(null);
  const [rows, setRows] = useState<EditableRow[]>([]);
  const [catalog, setCatalog] = useState<CatalogEntry[]>([]);
  const [catalogReady, setCatalogReady] = useState(false);
  const [openSuggestionsFor, setOpenSuggestionsFor] = useState<number | null>(null);
  const [suggestionsRect, setSuggestionsRect] = useState<{ top: number; left: number; width: number } | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as ShowEventDetail;
      setDungeonId(detail.dungeonId);
      setRefrId(detail.refrId);
      setRows((detail.current ?? []).map(toRow));
      setCatalog(detail.catalog ?? []);
      setCatalogReady(!!detail.catalogReady);
      setOpenSuggestionsFor(null);
      setVisible(true);
    };
    window.addEventListener('skymp5-client:showDungeonLootConfig', handler);
    return () => window.removeEventListener('skymp5-client:showDungeonLootConfig', handler);
  }, []);

  const send = (message: string) => {
    if ((window as any).skyrimPlatform?.sendMessage) {
      (window as any).skyrimPlatform.sendMessage(message);
    }
  };

  const close = () => setVisible(false);

  const updateRow = (key: number, patch: Partial<EditableRow>) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };

  const removeRow = (key: number) => {
    setRows((prev) => prev.filter((r) => r.key !== key));
  };

  const addRow = () => {
    setRows((prev) => [...prev, emptyRow()]);
  };

  const pickSuggestion = (rowKey: number, item: CatalogEntry) => {
    updateRow(rowKey, { label: item.n, baseIdHex: item.id.toString(16).padStart(8, '0') });
    setOpenSuggestionsFor(null);
  };

  const suggestionsFor = (row: EditableRow): CatalogEntry[] => {
    const q = row.label.trim().toLowerCase();
    if (!q) return [];
    const matches: CatalogEntry[] = [];
    for (const item of catalog) {
      if (item.n.toLowerCase().indexOf(q) !== -1) {
        matches.push(item);
        if (matches.length >= MAX_SUGGESTIONS) break;
      }
    }
    return matches;
  };

  const activeRow = useMemo(() => rows.find((r) => r.key === openSuggestionsFor) ?? null, [rows, openSuggestionsFor]);
  const activeSuggestions = activeRow ? suggestionsFor(activeRow) : [];

  const submit = () => {
    if (refrId === null) return;

    const entries = rows
      .map((r) => {
        const baseId = parseInt(r.baseIdHex.replace(/^0x/i, ''), 16);
        const weight = Number(r.weight);
        const minCount = Math.max(1, Math.floor(Number(r.minCount)));
        const maxCount = Math.max(minCount, Math.floor(Number(r.maxCount)));
        return { label: r.label, baseId, weight, minCount, maxCount };
      })
      .filter((e) => Number.isFinite(e.baseId) && e.baseId > 0 && Number.isFinite(e.weight) && e.weight > 0);

    send('dungeon-loot-config:' + JSON.stringify({ dungeonId, refrId, entries }));
    close();
  };

  if (!visible) return null;

  return (
    <div className="dungeon-loot-overlay">
      <div className="dungeon-loot-shell">

        <div className="dungeon-loot-header">
          <div className="dungeon-loot-header__row">
            <div className="dungeon-loot-header__text">
              <div className="dungeon-loot-header__brand">Donjon</div>
              <h2 className="dungeon-loot-header__title">Table de loot du coffre</h2>
            </div>
            <button type="button" className="dungeon-loot-header__close" onClick={close} aria-label="Fermer">
              ×
            </button>
          </div>
          <div className="dungeon-loot-header__subtitle">Donjon : {dungeonId}</div>
          {!catalogReady && (
            <div className="dungeon-loot-header__subtitle dungeon-loot-header__subtitle--warn">
              Catalogue d'objets en cours de chargement - saisis le BaseId à la main en attendant.
            </div>
          )}
        </div>

        <div className="dungeon-loot-body">
          <div className="dungeon-loot-table-head">
            <span className="dungeon-loot-table-head__cell dungeon-loot-table-head__cell--label">Nom (recherche ou repère perso)</span>
            <span className="dungeon-loot-table-head__cell dungeon-loot-table-head__cell--baseid">BaseId (hex)</span>
            <span className="dungeon-loot-table-head__cell dungeon-loot-table-head__cell--num">Chance (0-1)</span>
            <span className="dungeon-loot-table-head__cell dungeon-loot-table-head__cell--num">Qté min</span>
            <span className="dungeon-loot-table-head__cell dungeon-loot-table-head__cell--num">Qté max</span>
            <span className="dungeon-loot-table-head__cell dungeon-loot-table-head__cell--del" />
          </div>

          <div className="dungeon-loot-list" onScroll={() => setOpenSuggestionsFor(null)}>
            {rows.length === 0 && (
              <div className="dungeon-loot-empty">
                Aucune entrée - le coffre reste vide tant qu'aucune n'est ajoutée.
              </div>
            )}

            {rows.map((row) => (
              <div className="dungeon-loot-row-wrap" key={row.key}>
                <div className="dungeon-loot-row">
                  <input
                    className="dungeon-loot-input dungeon-loot-row__cell--label"
                    type="text"
                    value={row.label}
                    onChange={(e) => updateRow(row.key, { label: e.target.value })}
                    onFocus={(e) => {
                      setOpenSuggestionsFor(row.key);
                      const rect = e.currentTarget.getBoundingClientRect();
                      setSuggestionsRect({ top: rect.bottom, left: rect.left, width: rect.width });
                    }}
                    onBlur={() => setTimeout(() => setOpenSuggestionsFor((cur) => (cur === row.key ? null : cur)), 150)}
                    placeholder={catalogReady ? 'Chercher un objet...' : 'ex: Potion de soin'}
                  />
                  <input
                    className="dungeon-loot-input dungeon-loot-row__cell--baseid"
                    type="text"
                    value={row.baseIdHex}
                    onChange={(e) => updateRow(row.key, { baseIdHex: e.target.value })}
                    placeholder="0000000F"
                  />
                  <input
                    className="dungeon-loot-input dungeon-loot-row__cell--num"
                    type="number"
                    step="0.1"
                    min="0.01"
                    max="1"
                    value={row.weight}
                    onChange={(e) => updateRow(row.key, { weight: e.target.value })}
                    title="Chance que cette entrée apparaisse à chaque reset (0.1 = 10%, 1 = toujours)"
                  />
                  <input
                    className="dungeon-loot-input dungeon-loot-row__cell--num"
                    type="number"
                    min="1"
                    value={row.minCount}
                    onChange={(e) => updateRow(row.key, { minCount: e.target.value })}
                    title="Quantité minimum si l'entrée apparaît"
                  />
                  <input
                    className="dungeon-loot-input dungeon-loot-row__cell--num"
                    type="number"
                    min="1"
                    value={row.maxCount}
                    onChange={(e) => updateRow(row.key, { maxCount: e.target.value })}
                    title="Quantité maximum si l'entrée apparaît"
                  />
                  <button type="button" className="dungeon-loot-row-remove dungeon-loot-row__cell--del" onClick={() => removeRow(row.key)}>
                    ×
                  </button>
                </div>
              </div>
            ))}
          </div>

          {openSuggestionsFor !== null && activeSuggestions.length > 0 && suggestionsRect &&
            createPortal(
              <div
                className="dungeon-loot-suggestions"
                style={{ position: 'fixed', top: suggestionsRect.top, left: suggestionsRect.left, width: suggestionsRect.width }}
              >
                {activeSuggestions.map((item) => (
                  <button
                    type="button"
                    key={item.id}
                    className="dungeon-loot-suggestion"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pickSuggestion(openSuggestionsFor, item)}
                  >
                    <span className="dungeon-loot-suggestion__name">{item.n}</span>
                    <span className="dungeon-loot-suggestion__meta">{item.t} · {item.id.toString(16).padStart(8, '0')}</span>
                  </button>
                ))}
              </div>,
              document.body
            )}

          <button type="button" className="dungeon-loot-btn dungeon-loot-btn--secondary dungeon-loot-add-btn" onClick={addRow}>
            + Ajouter une entrée
          </button>
        </div>

        <div className="dungeon-loot-footer">
          <button type="button" className="dungeon-loot-btn dungeon-loot-btn--secondary" onClick={close}>
            Annuler
          </button>
          <button type="button" className="dungeon-loot-btn dungeon-loot-btn--primary" onClick={submit}>
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
};

export default DungeonLootConfig;
